import {
  deriveClientAddress,
  deriveClientPackageName,
  deriveServiceTypeDescription,
  deriveServiceTypeSelectorParams,
} from '~appointments/lib/derive';
import { initialiseUserOption } from '~appointments/lib/initialise';
import { onChangeClient, onChangePackageHandler, onChangeServiceType } from '~appointments/lib/onchange';
import { showClientHoldCheck, showLocations } from '~appointments/lib/show';
import {
  validateCareWorker,
  validateStartEndTimes,
  validateUserAvailability,
  validateUserQualifications,
} from '~appointments/lib/validate';

import { FieldProps } from '~components/form-fields';
import {
  FormInputCareWorkers,
  FormInputClientHoldCheck,
  FormInputDate,
  FormInputPackageSelector,
  FormInputQualifications,
  FormInputStartEndTimes,
} from '~appointments/components';

import { getAll as getLocations } from '~appointments/actions/locations';
import { getAll as getServiceTypes } from '~appointments/actions/serviceTypes';
import { getAll as getClients } from '~main/actions/clients';

import { selectCareQualificationLevels } from '~appointments/selectors/careQualificationLevels';
import { selectLocations } from '~appointments/selectors/locations';
import { selectAppointmentServiceTypes } from '~appointments/selectors/serviceTypes';
import { selectLookupClients } from '~main/selectors/clients/clients';

const groupCommon: FieldProps = {
  date: {
    type: 'custom',
    component: FormInputDate,
    caption: 'Appointment Date',
    required: true,
    colSize: 4,
    order: 100,
  },
  package_fixed_id: {
    type: 'custom',
    component: FormInputPackageSelector,
    dependencies: ['package_fixed_id', 'client_id', 'date'],
    onChange: onChangePackageHandler,
    required: true,
    colSize: 6,
    order: 170,
  },
  location_override_id: {
    type: 'select-lazy',
    caption: 'Location',
    colSize: 4,
    description: 'Optional, defaults to client home address',
    order: 200,
    selectorOptions: selectLocations,
    selectorActions: getLocations,
    selectorParams: { sort: ['name'], deleted: false },
    show: showLocations,
  },
  service_type_id: {
    type: 'select-lazy',
    caption: 'Service Type',
    initialOption: { id: 'service_type', name: '' },
    selectorOptions: selectAppointmentServiceTypes,
    selectorActions: getServiceTypes,
    selectorParams: deriveServiceTypeSelectorParams,
    onChange: onChangeServiceType,
    deriveWarning: deriveServiceTypeDescription,
    order: 300,
    colSize: 4,
    required: true,
    manualReload: true,
  },
  qualification_level: {
    type: 'custom',
    caption: 'Qualifications Required',
    component: FormInputQualifications,
    dependencies: ['service_type_id', 'qualification_level'],
    validateConfirm: validateUserQualifications,
    selectorOptions: selectCareQualificationLevels,
    order: 400,
  },

  start_end_time: {
    type: 'custom',
    caption: 'Start/End Times',
    colSize: 6,
    component: FormInputStartEndTimes,
    dependencies: ['date', 'start_time', 'end_time', 'ends_next_day'],
    validateConfirm: validateUserAvailability,
    validate: validateStartEndTimes,
    order: 600,
  },
  notes: {
    type: 'textarea',
    caption: 'Notes',
    placeholder: 'Instructions for care workers, specific to this appointment',
    order: 700,
  },
  client_id: {
    type: 'hidden',
  },
  user_id: {
    type: 'hidden',
  },
};

const newGroupClient: FieldProps = {
  ...groupCommon,
  date: {
    ...groupCommon.date,
    disabled: true,
  },
  client_name: {
    type: 'select-lazy',
    field: 'client_id',
    caption: 'Client / Facility',
    deriveDescription: deriveClientAddress,
    onChange: onChangeClient,
    className: 'text-left',
    required: true,
    colSize: 4,
    order: 150,
    selectorOptions: selectLookupClients,
    selectorActions: getClients,
    manualReload: true,
  },
  client_hold_check: {
    type: 'custom',
    component: FormInputClientHoldCheck,
    dependencies: ['date', 'client_id', 'repeat', 'repeat_start'],
    show: showClientHoldCheck,
    colSize: 4,
    order: 155,
  },
  client_package_name: {
    type: 'readonly',
    caption: 'Default Package',
    deriveValue: deriveClientPackageName,
    required: true,
    className: 'text-left',
    order: 160,
  },
  package_fixed_id: {
    type: 'custom',
    component: FormInputPackageSelector,
    dependencies: ['package_fixed_id', 'client_id', 'date'],
    onChange: onChangePackageHandler,
    required: true,
    colSize: 6,
    order: 170,
  },
  qualification_level: {
    type: 'hidden',
  },
  user_id: {
    type: 'hidden',
  },
};
export const newGroupUser: FieldProps = {
  ...groupCommon,
  date: {
    ...groupCommon.date,
    disabled: true,
  },
  client_name: {
    type: 'hidden',
  },
  package_fixed_id: {
    type: 'hidden',
  },
  user_id: {
    type: 'custom',
    caption: 'Care Worker',
    component: FormInputCareWorkers,
    dependencies: ['client_id', 'user_id', 'date', 'id', 'repeat'],
    validateConfirm: validateCareWorker,
    initialiseInitialValue: initialiseUserOption,
    colSize: 6,
    order: 450,
  },
};

export const newGroupParent: FieldProps = {
  ...groupCommon,
};

export default { newGroupParent, newGroupClient, newGroupUser };
